<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsLayout from '../../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../../settings/components/BaseSettingsHeader.vue';
import DiscountCodesAPI from 'dashboard/api/planning/discountCodes';

const { t } = useI18n();

const loading = ref(false);
const saving = ref(false);
const records = ref([]);
const searchQuery = ref('');
const dialogRef = ref(null);
const editingId = ref(null);

const form = reactive({
  name: '',
  code: '',
  discount_type: 'percent',
  value: 10,
  active: true,
  valid_until: '',
});

const typeOptions = computed(() => [
  { value: 'percent', label: t('PRICING.DISCOUNT_CODES.TYPE_PERCENT') },
  { value: 'fixed', label: t('PRICING.DISCOUNT_CODES.TYPE_FIXED') },
]);

const filteredRecords = computed(() => {
  if (!searchQuery.value) return records.value;
  const q = searchQuery.value.toLowerCase();
  return records.value.filter(
    row =>
      row.name?.toLowerCase().includes(q) || row.code?.toLowerCase().includes(q)
  );
});

const resetForm = () => {
  editingId.value = null;
  form.name = '';
  form.code = '';
  form.discount_type = 'percent';
  form.value = 10;
  form.active = true;
  form.valid_until = '';
};

const fetchRecords = async () => {
  loading.value = true;
  try {
    const res = await DiscountCodesAPI.getAll();
    records.value = res.data || [];
  } catch (e) {
    useAlert(t('PRICING.DISCOUNT_CODES.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  resetForm();
  dialogRef.value?.open();
};

const openEdit = row => {
  editingId.value = row.id;
  form.name = row.name;
  form.code = row.code;
  form.discount_type = row.discount_type;
  form.value = row.value;
  form.active = row.active;
  form.valid_until = row.valid_until || '';
  dialogRef.value?.open();
};

const closeDialog = () => {
  dialogRef.value?.close();
  resetForm();
};

const handleSave = async () => {
  saving.value = true;
  try {
    const payload = {
      name: form.name,
      code: form.code,
      discount_type: form.discount_type,
      value: Number(form.value),
      active: form.active,
      valid_until: form.valid_until || null,
    };
    if (editingId.value) {
      await DiscountCodesAPI.update(editingId.value, payload);
      useAlert(t('PRICING.DISCOUNT_CODES.UPDATE_SUCCESS'));
    } else {
      await DiscountCodesAPI.create(payload);
      useAlert(t('PRICING.DISCOUNT_CODES.CREATE_SUCCESS'));
    }
    closeDialog();
    await fetchRecords();
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        e?.response?.data?.message ||
        t('PRICING.DISCOUNT_CODES.SAVE_ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const handleDelete = async row => {
  try {
    await DiscountCodesAPI.delete(row.id);
    useAlert(t('PRICING.DISCOUNT_CODES.DELETE_SUCCESS'));
    await fetchRecords();
  } catch (e) {
    useAlert(t('PRICING.DISCOUNT_CODES.DELETE_ERROR'));
  }
};

const handleToggleActive = async row => {
  try {
    await DiscountCodesAPI.update(row.id, { active: !row.active });
    row.active = !row.active;
  } catch {
    useAlert(t('PRICING.DISCOUNT_CODES.SAVE_ERROR'));
  }
};

const formatValue = row =>
  row.discount_type === 'percent' ? `${row.value}%` : String(row.value);

const formatDate = dateStr => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

onMounted(fetchRecords);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('PRICING.LOADING')"
    :no-records-found="!loading && records.length === 0"
    :no-records-message="t('PRICING.DISCOUNT_CODES.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('PRICING.DISCOUNT_CODES.TITLE')"
        :description="t('PRICING.DISCOUNT_CODES.DESCRIPTION')"
      >
        <template #actions>
          <input
            v-model="searchQuery"
            type="text"
            class="reset-base rounded-lg border border-n-weak bg-n-solid-2 px-3 py-1.5 text-sm text-n-slate-12 placeholder:text-n-slate-10 w-52"
            :placeholder="t('PRICING.DISCOUNT_CODES.SEARCH_PLACEHOLDER')"
          />
          <Button
            icon="i-lucide-plus"
            md
            :label="t('PRICING.DISCOUNT_CODES.NEW')"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <tr class="border-b border-n-weak">
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ t('PRICING.DISCOUNT_CODES.NAME') }}
            </th>
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ t('PRICING.DISCOUNT_CODES.CODE') }}
            </th>
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ t('PRICING.DISCOUNT_CODES.VALUE') }}
            </th>
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ t('PRICING.DISCOUNT_CODES.VALID_UNTIL') }}
            </th>
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11 w-24"
            >
              {{ t('PRICING.TABLE.ACTIVE') }}
            </th>
            <th
              class="py-4 ltr:text-right rtl:text-left font-semibold text-n-slate-11 w-32"
            >
              {{ t('PRICING.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr
            v-for="row in filteredRecords"
            :key="row.id"
            class="border-b border-n-weak cursor-pointer"
            @click="openEdit(row)"
          >
            <td class="py-4 font-medium text-neutral-900 dark:text-white">
              {{ row.name }}
            </td>
            <td class="py-4 font-mono">
              {{ row.code }}
            </td>
            <td class="py-4">
              {{ formatValue(row) }}
            </td>
            <td class="py-4">
              {{ formatDate(row.valid_until) }}
            </td>
            <td class="py-4" @click.stop>
              <Switch
                :model-value="row.active"
                @change="handleToggleActive(row)"
              />
            </td>
            <td class="py-4" @click.stop>
              <div class="flex items-center justify-end gap-2">
                <Button
                  v-tooltip.top="t('PRICING.EDIT')"
                  icon="i-lucide-pen-line"
                  md
                  slate
                  faded
                  @click="openEdit(row)"
                />
                <Button
                  v-tooltip.top="t('PRICING.DELETE')"
                  icon="i-lucide-trash-2"
                  md
                  ruby
                  faded
                  @click="handleDelete(row)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </SettingsLayout>

  <Dialog
    ref="dialogRef"
    type="edit"
    width="lg"
    :title="
      editingId
        ? t('PRICING.DISCOUNT_CODES.EDIT_TITLE')
        : t('PRICING.DISCOUNT_CODES.NEW_TITLE')
    "
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="resetForm"
  >
    <div class="flex flex-col gap-3">
      <Input
        v-model="form.name"
        :label="t('PRICING.DISCOUNT_CODES.NAME')"
        type="text"
      />
      <Input
        v-model="form.code"
        :label="t('PRICING.DISCOUNT_CODES.CODE')"
        type="text"
      />
      <Input
        v-model="form.discount_type"
        type="select"
        :label="t('PRICING.DISCOUNT_CODES.TYPE')"
        :options="typeOptions"
      />
      <Input
        v-model="form.value"
        type="number"
        min="0"
        step="0.01"
        :label="t('PRICING.DISCOUNT_CODES.VALUE')"
      />
      <Input
        v-model="form.valid_until"
        type="date"
        :label="t('PRICING.DISCOUNT_CODES.VALID_UNTIL')"
      />
      <div class="flex items-center justify-between gap-3">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('PRICING.DISCOUNT_CODES.ACTIVE') }}
        </span>
        <Switch v-model="form.active" />
      </div>
    </div>
    <template #footer>
      <div class="flex w-full justify-end gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="t('PRICING.CANCEL')"
          type="button"
          @click="closeDialog"
        />
        <Button
          color="blue"
          :label="t('PRICING.DISCOUNT_CODES.SAVE')"
          :is-loading="saving"
          type="button"
          @click="handleSave"
        />
      </div>
    </template>
  </Dialog>
</template>
